<template>
  <div class="glass-container">
    <div class="gift-card-generator">
      <!-- Main Container -->
      <b-container fluid class="main-container">
        <!-- Header -->
        <div class="modern-card header-card">
          <div class="header-content">
            <div class="header-left">
              <div class="icon-wrapper">
                <b-icon-gift class="title-icon" />
              </div>
              <div>
                <h3 class="page-title">Gift Voucher Generator</h3>
                <p class="page-subtitle">Create percentage-based gift vouchers for your customers</p>
              </div>
            </div>
            <div class="header-stats" v-if="generatedCards.length > 0">
              <div class="stat-chip animate-slide-up" style="animation-delay: 0s;">
                <b-icon-card-list class="stat-icon" />
                <span class="chip-label">Generated</span>
                <span class="chip-value">{{ generatedCards.length }}</span>
              </div>
              <div class="stat-chip animate-slide-up" style="animation-delay: 0.1s;">
                <b-icon-tag class="stat-icon" />
                <span class="chip-label">Discount</span>
                <span class="chip-value">{{ roundAmount(perCardAmount) }}$</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Main 2-column layout: Form + Preview -->
        <div class="main-layout mt-4">
          <!-- Generation Form -->
          <div class="layout-col layout-col-form">
            <b-card class="glass-card form-card" no-body>
              <template #header>
                <div class="card-header-custom">
                  <div class="header-left">
                    <div class="header-icon-wrapper">
                      <b-icon-plus-circle class="header-icon" />
                    </div>
                    <h5 class="card-header-title">Generate Vouchers</h5>
                  </div>
                  <b-badge variant="light" class="step-badge">Step 1</b-badge>
                </div>
              </template>

              <b-card-body class="card-body-custom">
                <b-form @submit.prevent="generateGiftCards">
                  <!-- Single-line: Batch Name | Vouchers | Discount $ | Validity -->
                  <div class="single-line-form">
                    <b-form-group label="Batch Name" label-for="batch-name" class="single-field single-field-batch">
                      <b-input
                        id="batch-name"
                        v-model="form.batch_name"
                        placeholder="e.g., Grand Opening"
                        required
                        size="sm"
                        class="glass-input"
                      />
                    </b-form-group>

                    <b-form-group label="Vouchers" label-for="card-count" class="single-field single-field-count">
                      <b-input-group>
                        <b-input-group-prepend>
                          <b-button
                            size="sm"
                            variant="outline-secondary"
                            @click="form.count = Math.max(1, parseInt(form.count) - 1)"
                            class="counter-btn"
                          >
                            <b-icon-dash />
                          </b-button>
                        </b-input-group-prepend>
                        <b-form-input
                          id="card-count"
                          v-model="form.count"
                          type="number"
                          min="1"
                          max="50"
                          required
                          size="sm"
                          class="text-center glass-input"
                        />
                        <b-input-group-append>
                          <b-button
                            size="sm"
                            variant="outline-secondary"
                            @click="form.count = Math.min(50, parseInt(form.count) + 1)"
                            class="counter-btn"
                          >
                            <b-icon-plus />
                          </b-button>
                        </b-input-group-append>
                      </b-input-group>
                    </b-form-group>

                    <b-form-group label="Discount $" label-for="card-amount" class="single-field single-field-amount">
                      <b-input-group>
                        <b-input-group-prepend>
                          <span class="input-group-text glass-input-text">$</span>
                        </b-input-group-prepend>
                        <b-form-input
                          id="card-amount"
                          v-model="form.amount"
                          type="number"
                          min="5"
                          max="100"
                          step="5"
                          required
                          size="sm"
                          class="glass-input"
                        />
                      </b-input-group>
                    </b-form-group>

                    <b-form-group label="Validity" label-for="valid-until" class="single-field single-field-validity">
                      <div class="validity-inline">
                        <b-form-checkbox v-model="form.is_permanent" class="validity-checkbox glass-checkbox">
                          <b-icon-infinity class="mr-1" />
                        </b-form-checkbox>
                        <b-input-group class="validity-date-group">
                          <b-input-group-prepend>
                            <span class="input-group-text glass-input-text">
                              <b-icon-calendar3 />
                            </span>
                          </b-input-group-prepend>
                          <b-form-input
                            id="valid-until"
                            v-model="form.valid_until"
                            type="date"
                            size="sm"
                            :disabled="form.is_permanent"
                            class="glass-input"
                          />
                        </b-input-group>
                      </div>
                    </b-form-group>
                  </div>

                  <!-- Assign to Customer -->
                  <b-form-group label="Assign to Customer">
                    <div class="assign-customer-row">
                      <b-input-group>
                        <b-input-group-prepend>
                          <span class="input-group-text glass-input-text">
                            <b-icon-envelope />
                          </span>
                        </b-input-group-prepend>
                        <b-input
                          v-model="form.assigned_to_email"
                          placeholder="Customer email"
                          size="sm"
                          class="glass-input"
                        />
                      </b-input-group>
                      <b-input-group>
                        <b-input-group-prepend>
                          <span class="input-group-text glass-input-text">
                            <b-icon-person />
                          </span>
                        </b-input-group-prepend>
                        <b-input
                          v-model="form.assigned_to_name"
                          placeholder="Customer name"
                          size="sm"
                          class="glass-input"
                        />
                      </b-input-group>
                    </div>

                    <b-form-checkbox v-model="form.send_email" class="mt-2 send-email-checkbox glass-checkbox">
                      <b-icon-envelope-paper class="mr-1" /> Send voucher via email
                    </b-form-checkbox>
                  </b-form-group>

                  <!-- Design Template Picker (collapsible) -->
                  <div class="template-section">
                    <button
                      type="button"
                      class="template-toggle-btn"
                      @click="showTemplatePicker = !showTemplatePicker"
                    >
                      <span class="toggle-label">
                        <b-icon-palette class="mr-2" /> Card Design Template
                        <span class="current-theme-tag">
                          {{ currentThemeText }}
                        </span>
                      </span>
                      <b-icon
                        :icon="showTemplatePicker ? 'chevron-up' : 'chevron-down'"
                        class="toggle-chevron"
                      />
                    </button>

                    <div v-show="showTemplatePicker" class="template-section-body">
                      <div class="template-picker">
                        <div
                          v-for="theme in designThemes"
                          :key="theme.value"
                          class="template-option"
                          :class="{ active: form.design.theme === theme.value }"
                          @click="selectTheme(theme.value)"
                        >
                          <div class="template-swatch" :class="'swatch-' + theme.value">
                            <span class="swatch-amount">-$20</span>
                          </div>
                          <span class="template-name">{{ theme.text }}</span>
                        </div>
                      </div>

                      <small v-if="generatedCards.length > 0" class="text-muted d-block mt-2">
                        <b-icon-info-circle class="mr-1" /> Changing template updates the preview instantly
                      </small>

                      <b-input-group class="mt-2">
                        <b-input-group-prepend>
                          <span class="input-group-text glass-input-text">
                            <b-icon-chat />
                          </span>
                        </b-input-group-prepend>
                        <b-form-input
                          v-model="form.design.message"
                          placeholder="Personal message (optional)..."
                          size="sm"
                          class="glass-input"
                        />
                      </b-input-group>
                    </div>
                  </div>

                  <!-- Generate Button -->
                  <b-button
                    type="submit"
                    variant="success"
                    block
                    :disabled="isGenerating"
                    class="generate-btn"
                  >
                    <b-spinner small v-if="isGenerating" />
                    <template v-else>
                      <b-icon-gift-fill class="mr-2" />
                      Generate Vouchers
                    </template>
                  </b-button>

                  <!-- Quick Stats -->
                  <div v-if="form.count > 0" class="quick-stats">
                    <div class="stat-item animate-slide-up" style="animation-delay: 0s;">
                      <span class="stat-label">Total Value</span>
                      <span class="stat-number">${{ roundAmount(form.count * form.amount) }}</span>
                    </div>
                    <div class="stat-item animate-slide-up" style="animation-delay: 0.1s;">
                      <span class="stat-label">Vouchers</span>
                      <span class="stat-number">{{ form.count }}</span>
                    </div>
                    <div class="stat-item animate-slide-up" style="animation-delay: 0.2s;">
                      <span class="stat-label">Per Voucher</span>
                      <span class="stat-number">${{ roundAmount(form.amount) }}</span>
                    </div>
                  </div>
                </b-form>
              </b-card-body>
            </b-card>
          </div>

          <!-- Preview -->
          <div class="layout-col layout-col-preview">
            <b-card class="glass-card preview-card" no-body>
              <template #header>
                <div class="card-header-custom">
                  <div class="header-left">
                    <div class="header-icon-wrapper">
                      <b-icon-gift class="header-icon" />
                    </div>
                    <h5 class="card-header-title">Voucher Preview</h5>
                  </div>
                  <div class="header-actions">
                    <b-badge variant="info" class="voucher-count-badge">
                      <b-icon-card-list class="mr-1" /> {{ generatedCards.length }} Vouchers
                    </b-badge>
                    <b-button
                      v-if="generatedCards.length > 0"
                      variant="primary"
                      size="sm"
                      @click="printPreviewCards"
                      class="print-preview-btn"
                    >
                      <b-icon-printer class="mr-1" /> Print Preview
                    </b-button>
                  </div>
                </div>
              </template>

              <b-card-body>
                <div v-if="generatedCards.length === 0" class="empty-state">
                  <div class="empty-icon-wrapper">
                    <b-icon-gift scale="4" class="empty-icon" />
                  </div>
                  <h5 class="empty-title">No Vouchers Generated</h5>
                  <p class="empty-text">Fill in the form and click "Generate Vouchers"</p>
                </div>

                <div v-else>
                  <!-- Print Size Selector -->
                  <div class="print-size-selector">
                    <span class="selector-label">
                      <b-icon-grid class="mr-1" /> Vouchers per page:
                    </span>
                    <b-button-group size="sm">
                      <b-button
                        v-for="size in printSizes"
                        :key="size"
                        :variant="printSize === size ? 'primary' : 'outline-primary'"
                        @click="printSize = size"
                        class="size-btn"
                      >
                        {{ size }}
                      </b-button>
                    </b-button-group>
                  </div>

                  <!-- Vouchers Grid -->
                  <div class="vouchers-grid" id="preview-cards-container">
                    <div
                      v-for="(card, index) in generatedCards"
                      :key="index"
                      class="voucher-card animate-slide-up"
                      :style="{ animationDelay: (index * 0.05) + 's' }"
                    >
                      <div class="voucher-card-inner" :class="'theme-' + (card.design_theme || form.design.theme || 'classic')">
                        <div class="voucher-top-row">
                          <span class="voucher-brand-pill">FURNITURE MECCA</span>
                          <span class="voucher-type-pill"><b-icon-gift-fill class="mr-1" />GIFT VOUCHER</span>
                        </div>
                        <div class="voucher-batch-pill" v-if="form.batch_name || card.batch_name">
                          {{ form.batch_name || card.batch_name }}
                        </div>
                        <div class="voucher-amount">
                          <span class="voucher-amount-symbol">$</span>{{ roundAmount(card.amount) }}
                        </div>
                        <div class="voucher-description">ON YOUR NEXT PURCHASE</div>
                        <div class="voucher-code-box">
                          # {{ card.code }}
                        </div>
                        <div class="voucher-footer-row">
                          <span class="footer-item">
                            <b-icon-calendar3 class="mr-1" />
                            <template v-if="card.valid_until">Valid until: {{ formatDate(card.valid_until) }}</template>
                            <template v-else>No expiration</template>
                          </span>
                          <span class="footer-item">
                            <b-icon-exclamation-circle class="mr-1" /> ONE TIME USE ONLY
                          </span>
                          <span class="footer-item" v-if="card.assigned_to_email">
                            <b-icon-envelope class="mr-1" /> {{ card.assigned_to_email }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Action Bar -->
                  <div class="action-bar">
                    <b-button variant="primary" size="sm" @click="printPreviewCards">
                      <b-icon-printer class="mr-1" /> Print All ({{ printSize }} per page)
                    </b-button>
                    <b-button variant="info" size="sm" @click="sendEmails" :disabled="!hasAssignedEmails">
                      <b-icon-envelope class="mr-1" /> Email
                    </b-button>
                    <b-button variant="success" size="sm" @click="downloadCSV">
                      <b-icon-download class="mr-1" /> CSV
                    </b-button>
                    <b-button variant="outline-secondary" size="sm" @click="clearCards">
                      <b-icon-x class="mr-1" /> Clear All
                    </b-button>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </div>
        </div>

        <!-- Recent Batches -->
        <div class="mt-4">
          <b-card class="glass-card table-card" no-body>
            <template #header>
              <div class="card-header-custom">
                <div class="header-left">
                  <div class="header-icon-wrapper">
                    <b-icon-clock-history class="header-icon" />
                  </div>
                  <h5 class="card-header-title">Recent Batches</h5>
                </div>
                <div class="header-actions">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="loadBatches"
                    :disabled="loading"
                    class="mr-2"
                  >
                    <b-spinner small v-if="loading" />
                    <b-icon-arrow-repeat v-else class="mr-1" /> Refresh
                  </b-button>
                  <b-button
                    v-if="selectedBatch"
                    variant="success"
                    size="sm"
                    @click="printSelectedBatch"
                    class="print-batch-btn"
                  >
                    <b-icon-printer class="mr-1" /> Print Selected
                  </b-button>
                </div>
              </div>
            </template>

            <b-table
              :items="batches"
              :fields="batchFields"
              striped
              hover
              responsive
              selectable
              select-mode="single"
              @row-selected="onBatchSelected"
              class="glass-table"
            >
              <template #cell(selected)="data">
                <b-form-checkbox
                  :checked="data.rowSelected"
                  @change="(checked) => selectBatch(data.item, checked)"
                  class="batch-select-checkbox"
                />
              </template>

              <template #cell(per_card_amount)="data">
                {{ roundAmount(data.value) }}$
              </template>

              <template #cell(status)="data">
                <b-badge :variant="getStatusVariant(data.value)" class="status-badge">
                  <b-icon-circle-fill class="status-dot" /> {{ data.value }}
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <b-button
                  size="sm"
                  variant="outline-info"
                  @click="viewBatch(data.item)"
                  class="action-icon-btn"
                  title="View Batch"
                >
                  <b-icon-eye />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-success"
                  @click="printBatch(data.item)"
                  class="action-icon-btn"
                  title="Print Batch"
                >
                  <b-icon-printer />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="printBatch(data.item, true)"
                  class="action-icon-btn"
                  title="Export as PDF"
                >
                  <b-icon-file-pdf />
                </b-button>
              </template>
            </b-table>

            <div v-if="batches.length === 0" class="text-center py-4 text-muted">
              <b-icon-inbox class="mr-2" /> No batches found
            </div>
          </b-card>
        </div>
      </b-container>
    </div>

    <!-- Batch Print Modal -->
    <b-modal
      id="batch-print-modal"
      size="xl"
      title="Batch Print Preview"
      centered
      @hidden="closePrintModal"
      class="glass-modal"
    >
      <div class="batch-print-container" ref="batchPrintContent">
        <!-- Print options (collapsible) -->
        <div class="batch-print-options-collapsible no-print">
          <button
            type="button"
            class="batch-print-toggle-btn"
            @click="showBatchPrintOptions = !showBatchPrintOptions"
          >
            <span class="toggle-label">
              <b-icon-sliders class="mr-2" /> Print Options
              <span class="current-theme-tag">
                {{ batchThemeText }}
              </span>
              <span class="current-size-tag">
                {{ batchPrintSize }} / page
              </span>
            </span>
            <b-icon
              :icon="showBatchPrintOptions ? 'chevron-up' : 'chevron-down'"
              class="toggle-chevron"
            />
          </button>

          <div v-show="showBatchPrintOptions" class="batch-print-options-body">
            <div class="batch-option-block">
              <span class="selector-label"><b-icon-palette class="mr-1" /> Card Design:</span>
              <div class="template-picker template-picker-sm">
                <div
                  v-for="theme in designThemes"
                  :key="'batch-' + theme.value"
                  class="template-option"
                  :class="{ active: batchPrintTheme === theme.value }"
                  @click="batchPrintTheme = theme.value"
                >
                  <div class="template-swatch" :class="'swatch-' + theme.value">
                    <span class="swatch-amount">-$</span>
                  </div>
                  <span class="template-name">{{ theme.text }}</span>
                </div>
              </div>
            </div>

            <div class="batch-option-block">
              <span class="selector-label"><b-icon-grid class="mr-1" /> Vouchers per page:</span>
              <b-button-group size="sm">
                <b-button
                  v-for="size in printSizes"
                  :key="'batch-size-' + size"
                  :variant="batchPrintSize === size ? 'primary' : 'outline-primary'"
                  @click="batchPrintSize = size"
                  class="size-btn"
                >
                  {{ size }}
                </b-button>
              </b-button-group>
            </div>
          </div>
        </div>

        <!-- Live themed preview matching main preview cards -->
        <div class="print-vouchers-grid" :style="{ gridTemplateColumns: batchGridCols }">
          <div v-if="printCardsData.length === 0" class="text-center py-4" style="grid-column: 1 / -1;">
            <b-icon-inbox scale="2" class="text-muted" />
            <p>No vouchers found in this batch</p>
          </div>
          <div
            v-for="(card, index) in printCardsData"
            :key="index"
            class="print-voucher-item"
          >
            <div class="voucher-card-inner" :class="'theme-' + batchPrintTheme">
              <div class="voucher-top-row">
                <span class="voucher-brand-pill">FURNITURE MECCA</span>
                <span class="voucher-type-pill"><b-icon-gift-fill class="mr-1" />GIFT VOUCHER</span>
              </div>
              <div class="voucher-batch-pill" v-if="printBatchData && printBatchData.name">
                {{ printBatchData.name }}
              </div>
              <div class="voucher-amount">
                <span class="voucher-amount-symbol">$</span>{{ roundAmount(card.amount) }}
              </div>
              <div class="voucher-description">ON YOUR NEXT PURCHASE</div>
              <div class="voucher-code-box"># {{ card.code }}</div>
              <div class="voucher-footer-row">
                <span class="footer-item">
                  <b-icon-calendar3 class="mr-1" />
                  <template v-if="card.valid_until">Valid until: {{ formatDate(card.valid_until) }}</template>
                  <template v-else>No expiration</template>
                </span>
                <span class="footer-item">
                  <b-icon-exclamation-circle class="mr-1" /> ONE TIME USE ONLY
                </span>
                <span class="footer-item" v-if="card.assigned_to_email">
                  <b-icon-envelope class="mr-1" /> {{ card.assigned_to_email }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <hr class="print-divider" />

        <div class="print-footer">
          <p><b-icon-heart class="mr-1" /> Thank you for choosing Furniture Mecca!</p>
          <p class="print-footer-small">This is a system-generated document. Please verify all information.</p>
          <p class="print-footer-small">© {{ new Date().getFullYear() }} Furniture Mecca. All rights reserved.</p>
        </div>
      </div>

      <template #modal-footer>
        <div class="print-modal-footer">
          <b-button variant="secondary" @click="$bvModal.hide('batch-print-modal')">
            <b-icon-x class="mr-1" /> Close
          </b-button>
          <b-button variant="primary" @click="printBatchPreview(false)">
            <b-icon-printer class="mr-1" /> Print ({{ batchPrintSize }} per page)
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import moment from 'moment';

export default {
  name: 'GenerateGiftCards',
  data() {
    return {
      isGenerating: false,
      loading: false,
      generatedCards: [],
      batches: [],
      printBatchData: null,
      printCardsData: [],
      selectedBatch: null,
      batchPrintTheme: 'classic',
      batchPrintSize: 6,

      printSizes: [4, 6, 8, 10],
      printSize: 6,
      showTemplatePicker: false,
      showBatchPrintOptions: false,

      form: {
        count: 1,
        amount: 20,
        batch_name: '',
        valid_until: null,
        is_permanent: true,
        category_id: null,
        subcategory_id: null,
        vendor_id: null,
        assigned_to_email: '',
        assigned_to_name: '',
        send_email: false,
        notes: '',
        design: {
          theme: 'classic',
          message: ''
        }
      },

      designThemes: [
        { value: 'classic', text: 'Classic' },
        { value: 'modern', text: 'Modern' },
        { value: 'elegant', text: 'Elegant' },
        { value: 'holiday', text: 'Holiday' },
        { value: 'minimal', text: 'Minimal' }
      ],

      categoryOptions: [],
      subcategoryOptions: [],
      vendorOptions: [],

      batchFields: [
        { key: 'selected', label: '' },
        { key: 'batch_id', label: 'Batch ID' },
        { key: 'name', label: 'Name' },
        { key: 'number_of_cards', label: 'Vouchers' },
        { key: 'per_card_amount', label: 'Discount' },
        { key: 'status', label: 'Status' },
        { key: 'created_at', label: 'Created' },
        { key: 'actions', label: 'Actions' }
      ]
    };
  },

  computed: {
    totalAmount() {
      return this.generatedCards.reduce((sum, card) => sum + card.amount, 0);
    },
    perCardAmount() {
      return this.generatedCards.length > 0 ? this.generatedCards[0].amount : 0;
    },
    hasAssignedEmails() {
      return this.generatedCards.some(card => card.assigned_to_email);
    },
    batchGridCols() {
      return 'repeat(2, 1fr)';
    },
    currentThemeText() {
      const found = this.designThemes.find(t => t.value === this.form.design.theme);
      return found ? found.text : 'Classic';
    },
    batchThemeText() {
      const found = this.designThemes.find(t => t.value === this.batchPrintTheme);
      return found ? found.text : 'Classic';
    }
  },

  created() {
    this.loadPrereqs();
    this.loadBatches();
  },

  methods: {
    roundAmount(value) {
      return Math.round(value);
    },

    selectTheme(theme) {
      this.form.design.theme = theme;
      if (this.generatedCards.length > 0) {
        this.generatedCards = this.generatedCards.map(c => ({
          ...c,
          design_theme: theme
        }));
      }
    },

    async loadPrereqs() {
      try {
        const cats = await Requestor.getAsyncP('/api/category/get_all_categories/', {}, this);
        if (cats.data.result) {
          this.categoryOptions = cats.data.result.map(c => ({
            value: c.category_id,
            text: c.category_name
          }));
        }

        const vendors = await Requestor.getAsyncP('/api/user/get_tools_packet/', {}, this);
        if (vendors.data.result && vendors.data.result.vendors) {
          this.vendorOptions = vendors.data.result.vendors.map(v => ({
            value: v.value,
            text: v.text
          }));
        }
      } catch (error) {
        console.error('Error loading prerequisites:', error);
      }
    },

    async generateGiftCards() {
      this.isGenerating = true;

      try {
        const data = {
          count: parseInt(this.form.count),
          amount: parseFloat(this.form.amount),
          batch_name: this.form.batch_name,
          valid_until: this.form.is_permanent ? null : this.form.valid_until,
          is_permanent: this.form.is_permanent,
          category_id: this.form.category_id,
          subcategory_id: this.form.subcategory_id,
          vendor_id: this.form.vendor_id,
          assigned_to_email: this.form.assigned_to_email || null,
          assigned_to_name: this.form.assigned_to_name || null,
          send_email: this.form.send_email,
          notes: this.form.notes,
          design_data: {
            theme: this.form.design.theme,
            message: this.form.design.message
          }
        };

        const response = await Requestor.postAsyncP('/api/giftcard/generate/', data, this);

        if (response.data.error) {
          this.$bvToast.toast(response.data.error, {
            title: 'Error',
            variant: 'danger'
          });
        } else {
          const cards = response.data.result.cards || [];
          const selectedTheme = this.form.design.theme || 'classic';
          this.generatedCards = cards.map(c => ({
            ...c,
            design_theme: c.design_theme || (c.design_data && c.design_data.theme) || selectedTheme
          }));

          this.$bvToast.toast(
            `Successfully generated ${this.generatedCards.length} gift vouchers!`,
            {
              title: 'Success',
              variant: 'success'
            }
          );

          this.loadBatches();
        }
      } catch (error) {
        console.error('Error generating gift cards:', error);
        this.$bvToast.toast('Failed to generate gift cards', {
          title: 'Error',
          variant: 'danger'
        });
      } finally {
        this.isGenerating = false;
      }
    },

    async loadBatches() {
      this.loading = true;

      try {
        const response = await Requestor.getAsyncP('/api/giftcard/get_batches/', {}, this);
        if (response.data.result) {
          this.batches = response.data.result;
        }
      } catch (error) {
        console.error('Error loading batches:', error);
      } finally {
        this.loading = false;
      }
    },

    async viewBatch(batch) {
      this.$router.push(`/gift-card/batch/${batch.batch_id}`);
    },

    onBatchSelected(items) {
      this.selectedBatch = items.length > 0 ? items[0] : null;
    },

    selectBatch(item, checked) {
      if (checked) {
        this.selectedBatch = item;
      } else {
        this.selectedBatch = null;
      }
      this.$forceUpdate();
    },

    async printSelectedBatch() {
      if (!this.selectedBatch) {
        this.$bvToast.toast('Please select a batch first.', { title: 'Info', variant: 'warning' });
        return;
      }
      await this.printBatch(this.selectedBatch);
    },

    async printBatch(batch, isPdf = false) {
      if (!batch || !batch.batch_id) {
        this.$bvToast.toast('Batch ID not found.', { title: 'Error', variant: 'danger' });
        return;
      }

      try {
        let response = await Requestor.getAsyncP('/api/giftcard/get_gift_cards/', {
          batch_id: batch.batch_id
        }, this);

        if (!response.data.result || response.data.result.length === 0) {
          response = await Requestor.getAsyncP('/api/giftcard/get_gift_cards/', {
            batch_id: batch.id
          }, this);
        }

        this.printBatchData = batch;
        this.printCardsData = response.data.result || [];
        this.batchPrintTheme = this.form.design.theme || 'classic';
        this.batchPrintSize = this.printSize || 6;

        if (this.printCardsData.length === 0) {
          this.$bvToast.toast('No cards found in this batch.', { title: 'Info', variant: 'warning' });
          return;
        }

        if (isPdf) {
          this.printBatchPreview(true);
        } else {
          this.$bvModal.show('batch-print-modal');
        }
      } catch (error) {
        console.error('Error loading batch cards:', error);
        this.$bvToast.toast('Failed to load batch cards.', { title: 'Error', variant: 'danger' });
      }
    },

    closePrintModal() {
      this.printBatchData = null;
      this.printCardsData = [];
      this.batchPrintTheme = 'classic';
      this.batchPrintSize = 6;
    },

    printPreviewCards() {
      if (this.generatedCards.length === 0) {
        this.$bvToast.toast('No vouchers to print.', { title: 'Info', variant: 'warning' });
        return;
      }

      const sizeMap = {
        4: { padding: '16px 18px 12px', fontSize: '36px' },
        6: { padding: '14px 16px 10px', fontSize: '32px' },
        8: { padding: '12px 14px 8px', fontSize: '28px' },
        10: { padding: '10px 12px 6px', fontSize: '24px' }
      };

      const colMap = {
        4: 'repeat(2, 1fr)',
        6: 'repeat(2, 1fr)',
        8: 'repeat(2, 1fr)',
        10: 'repeat(2, 1fr)'
      };

      const size = sizeMap[this.printSize] || sizeMap[6];
      const gridCols = colMap[this.printSize] || colMap[6];

      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      const doc = printWindow.document;

      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Gift Vouchers - ${this.form.batch_name || 'Preview'}</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              padding: 20px; 
              background: white;
            }
            
            .print-header {
              text-align: center;
              padding: 15px 0 25px;
              border-bottom: 3px solid #2c3e50;
              margin-bottom: 25px;
            }
            .print-header h1 {
              font-size: 28px;
              font-weight: 700;
              color: #2c3e50;
              letter-spacing: 3px;
            }
            .print-header .subtitle {
              color: #7f8c8d;
              font-size: 13px;
              margin-top: 4px;
            }
            .print-header .batch-info {
              display: flex;
              justify-content: center;
              gap: 15px;
              margin-top: 10px;
              flex-wrap: wrap;
            }
            .print-header .batch-info span {
              background: #e9ecef;
              padding: 3px 14px;
              border-radius: 20px;
              font-size: 12px;
              color: #555;
            }
            
            .vouchers-grid {
              display: grid;
              grid-template-columns: ${gridCols};
              gap: ${this.printSize <= 6 ? '20px' : '12px'};
              max-width: 1200px;
              margin: 0 auto;
              page-break-after: always;
            }

            .voucher-card-inner {
              background: #faf7f2;
              border-radius: 14px;
              padding: ${size.padding};
              text-align: center;
              border: 1px solid #e8e0d8;
              position: relative;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: center;
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .voucher-top-row {
              display: flex; justify-content: space-between; align-items: center;
              width: 100%; margin-bottom: 8px;
            }
            .voucher-brand-pill {
               border: 1.5px solid #1e3a5f; color: #1e3a5f; font-size: 10px; font-weight: 700;
              letter-spacing: 0.5px; padding: 3px 10px; border-radius: 4px; text-transform: uppercase;
            }
            .voucher-type-pill {
              border: 1.5px solid #1e3a5f; color: #1e3a5f; font-size: 9px; font-weight: 700;
              letter-spacing: 0.5px; padding: 2px 8px; border-radius: 4px; background: #fff;
            }
            .voucher-batch-pill {
              background: #2563eb; color: #fff; font-size: 10px; font-weight: 700;
              letter-spacing: 1px; padding: 2px 12px; border-radius: 4px; text-transform: uppercase;
              margin-bottom: 2px;
            }
            .voucher-amount {
              font-size: ${size.fontSize}; font-weight: 800; color: #1a1a2e; line-height: 1.1; margin: 2px 0;
            }
            .voucher-amount-symbol { font-size: calc(${size.fontSize} * 0.65); font-weight: 700; color: #1a1a2e; }
            .voucher-description {
              font-size: 11px; font-weight: 600; color: #6c7a8d; letter-spacing: 2px;
              text-transform: uppercase; margin: 2px 0 8px;
            }
            .voucher-code-box {
              font-size: 11px; font-family: 'Courier New', monospace; font-weight: 600;
              color: #1e3a5f; border: 1.5px dashed #93c5fd; background: #eff6ff;
              padding: 4px 14px; border-radius: 20px; letter-spacing: 0.5px; margin-bottom: 10px;
            }
            .voucher-footer-row {
              display: flex; justify-content: center; flex-wrap: wrap; gap: 8px 14px;
              width: 100%; padding-top: 8px; border-top: 1px solid #ebe4dc;
              font-size: 10px; color: #6c7a8d;
            }
            .footer-item { display: inline-flex; align-items: center; gap: 3px; }

            .theme-classic { background: #faf7f2; border-color: #e8e0d8; }
            .theme-classic .voucher-brand-pill, .theme-classic .voucher-batch-pill { border-color: #1e3a5f; color: #1e3a5f;}
            .theme-classic .voucher-type-pill { border-color: #1e3a5f; color: #1e3a5f; }
            .theme-classic .voucher-amount, .theme-classic .voucher-amount-symbol { color: #1a1a2e; }
            .theme-classic .voucher-code-box { border-color: #93c5fd; background: #eff6ff; color: #1e3a5f; }

            .theme-modern { background: linear-gradient(145deg, #eef2f7, #e2e8f0); border-color: #94a3b8; }
            .theme-modern .voucher-brand-pill, .theme-modern .voucher-batch-pill { background: #1e40af; color: #fff; }
            .theme-modern .voucher-type-pill { border-color: #1e40af; color: #1e40af; }
            .theme-modern .voucher-amount, .theme-modern .voucher-amount-symbol { color: #0f172a; }
            .theme-modern .voucher-description { color: #475569; }
            .theme-modern .voucher-code-box { border-color: #60a5fa; background: #dbeafe; color: #1e3a8a; }
            .theme-modern .voucher-footer-row { border-color: #cbd5e1; color: #64748b; }

            .theme-elegant { background: linear-gradient(145deg, #1a1a2e, #16213e); border-color: #c9a227; }
            .theme-elegant .voucher-brand-pill, .theme-elegant .voucher-batch-pill { background: #c9a227; color: #1a1a2e; }
            .theme-elegant .voucher-type-pill { border-color: #c9a227; color: #c9a227; background: transparent; }
            .theme-elegant .voucher-amount, .theme-elegant .voucher-amount-symbol { color: #f5d76e; }
            .theme-elegant .voucher-description { color: #d4c5a0; }
            .theme-elegant .voucher-code-box { border-color: #c9a227; background: rgba(201,162,39,0.12); color: #f5d76e; }
            .theme-elegant .voucher-footer-row { border-color: #3d3a2e; color: #b8a88a; }

            .theme-holiday { background: linear-gradient(145deg, #fff5f5, #fef2f2); border-color: #c53030; }
            .theme-holiday .voucher-brand-pill, .theme-holiday .voucher-batch-pill { background: #c53030; color: #fff; }
            .theme-holiday .voucher-type-pill { border-color: #276749; color: #276749; }
            .theme-holiday .voucher-amount, .theme-holiday .voucher-amount-symbol { color: #9b2c2c; }
            .theme-holiday .voucher-description { color: #276749; }
            .theme-holiday .voucher-code-box { border-color: #fc8181; background: #fff5f5; color: #9b2c2c; }
            .theme-holiday .voucher-footer-row { border-color: #feb2b2; color: #9b2c2c; }

            .theme-minimal { background: #ffffff; border-color: #1a1a1a; border-width: 1.5px; }
            .theme-minimal .voucher-brand-pill, .theme-minimal .voucher-batch-pill { background: #111; color: #fff; }
            .theme-minimal .voucher-type-pill { border-color: #111; color: #111; background: transparent; }
            .theme-minimal .voucher-amount, .theme-minimal .voucher-amount-symbol { color: #111; }
            .theme-minimal .voucher-description { color: #555; }
            .theme-minimal .voucher-code-box { border-color: #999; background: #f5f5f5; color: #111; }
            .theme-minimal .voucher-footer-row { border-color: #e5e5e5; color: #666; }

            .print-footer {
              text-align: center;
              padding: 20px 0 10px;
              border-top: 2px solid #e8eaed;
              margin-top: 25px;
            }
            .print-footer p { color: #7f8c8d; font-size: 12px; margin: 4px 0; }
            .print-footer .footer-small { font-size: 10px; color: #95a5a6; }
            
            @media print {
              body { padding: 10px; background: white; }
              .voucher-card-inner { 
                box-shadow: none; 
                border: 1px solid #ddd;
                break-inside: avoid;
                page-break-inside: avoid;
              }
              .vouchers-grid:last-child {
                page-break-after: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>🎁 FURNITURE MECCA — GIFT VOUCHERS</h1>
            <p class="subtitle">Present this voucher to redeem your discount</p>
            <div class="batch-info">
              <span>📋 Batch: ${this.form.batch_name || 'Unnamed'}</span>
              <span>📅 Generated: ${new Date().toLocaleDateString()}</span>
              <span>🏷️ Discount: ${this.roundAmount(this.perCardAmount)}$</span>
              <span>📊 Total: ${this.generatedCards.length} vouchers</span>
              <span>📐 ${this.printSize} per page</span>
            </div>
          </div>
      `;

      const perPage = this.printSize;
      const totalCards = this.generatedCards.length;

      for (let i = 0; i < totalCards; i++) {
        if (i % perPage === 0) {
          if (i > 0) {
            htmlContent += `</div>`;
          }
          htmlContent += `<div class="vouchers-grid">`;
        }

        const card = this.generatedCards[i];
        const validityText = card.valid_until
          ? 'Valid until: ' + new Date(card.valid_until).toLocaleDateString()
          : 'No expiration';
        const assignedHtml = card.assigned_to_email
          ? `<span class="footer-item">✉ ${card.assigned_to_name || card.assigned_to_email}</span>`
          : '';
        const theme = card.design_theme || this.form.design.theme || 'classic';
        const batchLabel = this.form.batch_name || 'GIFT';

        htmlContent += `
          <div class="voucher-card-inner theme-${theme}">
            <div class="voucher-top-row">
              <span class="voucher-brand-pill">FURNITURE MECCA</span>
              <span class="voucher-type-pill">🎁 GIFT VOUCHER</span>
            </div>
            <div class="voucher-batch-pill">${batchLabel}</div>
            <div class="voucher-amount"><span class="voucher-amount-symbol">$</span>${this.roundAmount(card.amount)}</div>
            <div class="voucher-description">ON YOUR NEXT PURCHASE</div>
            <div class="voucher-code-box"># ${card.code}</div>
            <div class="voucher-footer-row">
              <span class="footer-item">📅 ${validityText}</span>
              <span class="footer-item">⚠ ONE TIME USE ONLY</span>
              ${assignedHtml}
            </div>
          </div>
        `;
      }

      htmlContent += `
          </div>
          <div class="print-footer">
            <p>❤️ Thank you for choosing Furniture Mecca!</p>
            <p class="footer-small">This is a system-generated document. Please verify all information.</p>
            <p class="footer-small">© ${new Date().getFullYear()} Furniture Mecca. All rights reserved.</p>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 2000);
            };
          <\/script>
        </body>
        </html>
      `;

      doc.write(htmlContent);
      doc.close();
    },

    printBatchPreview(isPdf = false) {
      if (!this.printCardsData || this.printCardsData.length === 0) {
        this.$bvToast.toast('No vouchers to print.', { title: 'Info', variant: 'warning' });
        return;
      }

      const sizeMap = {
        4: { padding: '16px 18px 12px', fontSize: '36px' },
        6: { padding: '14px 16px 10px', fontSize: '32px' },
        8: { padding: '12px 14px 8px', fontSize: '28px' },
        10: { padding: '10px 12px 6px', fontSize: '24px' }
      };
      const colMap = {
        4: 'repeat(2, 1fr)',
        6: 'repeat(2, 1fr)',
        8: 'repeat(2, 1fr)',
        10: 'repeat(2, 1fr)'
      };

      const perPage = this.batchPrintSize || 6;
      const size = sizeMap[perPage] || sizeMap[6];
      const gridCols = colMap[perPage] || colMap[6];
      const theme = this.batchPrintTheme || 'classic';
      const batchName = this.printBatchData ? (this.printBatchData.name || this.printBatchData.batch_id) : 'Batch';
      const batchId = this.printBatchData ? this.printBatchData.batch_id : 'N/A';
      const discount = this.printBatchData ? this.roundAmount(this.printBatchData.per_card_amount) : 0;
      const created = this.printBatchData && this.printBatchData.created_at
        ? new Date(this.printBatchData.created_at).toLocaleDateString()
        : new Date().toLocaleDateString();

      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      const doc = printWindow.document;

      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Gift Vouchers - ${batchId}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; background: white; }

            .print-header {
              text-align: center;
              padding: 15px 0 25px;
              border-bottom: 3px solid #2c3e50;
              margin-bottom: 25px;
            }
            .print-header h1 {
              font-size: 28px;
              font-weight: 700;
              color: #2c3e50;
              letter-spacing: 3px;
            }
            .print-header .subtitle { color: #7f8c8d; font-size: 13px; margin-top: 4px; }
            .print-header .batch-info {
              display: flex; justify-content: center; gap: 15px; margin-top: 10px; flex-wrap: wrap;
            }
            .print-header .batch-info span {
              background: #e9ecef; padding: 3px 14px; border-radius: 20px; font-size: 12px; color: #555;
            }

            .vouchers-grid {
              display: grid;
              grid-template-columns: ${gridCols};
              gap: ${perPage <= 6 ? '20px' : '12px'};
              max-width: 1200px;
              margin: 0 auto;
              page-break-after: always;
            }

            .voucher-card-inner {
              background: #faf7f2;
              border-radius: 14px;
              padding: ${size.padding};
              text-align: center;
              border: 1px solid #e8e0d8;
              position: relative;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: center;
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .voucher-top-row {
              display: flex; justify-content: space-between; align-items: center;
              width: 100%; margin-bottom: 8px;
            }
            .voucher-brand-pill {
              border: 1.5px solid #1e3a5f; color: #1e3a5f; font-size: 10px; font-weight: 700;
              letter-spacing: 0.5px; padding: 3px 10px; border-radius: 4px; text-transform: uppercase;
            }
            .voucher-type-pill {
              border: 1.5px solid #1e3a5f; color: #1e3a5f; font-size: 9px; font-weight: 700;
              letter-spacing: 0.5px; padding: 2px 8px; border-radius: 4px; background: #fff;
            }
            .voucher-batch-pill {
              background: #2563eb; color: #fff; font-size: 10px; font-weight: 700;
              letter-spacing: 1px; padding: 2px 12px; border-radius: 4px; text-transform: uppercase;
              margin-bottom: 2px;
            }
            .voucher-amount {
              font-size: ${size.fontSize}; font-weight: 800; color: #1a1a2e; line-height: 1.1; margin: 2px 0;
            }
            .voucher-amount-symbol { font-size: 22px; font-weight: 700; color: #1a1a2e; }
            .voucher-description {
              font-size: 11px; font-weight: 600; color: #6c7a8d; letter-spacing: 2px;
              text-transform: uppercase; margin: 2px 0 8px;
            }
            .voucher-code-box {
              font-size: 11px; font-family: 'Courier New', monospace; font-weight: 600;
              color: #1e3a5f; border: 1.5px dashed #93c5fd; background: #eff6ff;
              padding: 4px 14px; border-radius: 20px; letter-spacing: 0.5px; margin-bottom: 10px;
            }
            .voucher-footer-row {
              display: flex; justify-content: center; flex-wrap: wrap; gap: 8px 14px;
              width: 100%; padding-top: 8px; border-top: 1px solid #ebe4dc;
              font-size: 10px; color: #6c7a8d;
            }
            .footer-item { display: inline-flex; align-items: center; gap: 3px; }

            .theme-classic { background: #faf7f2; border-color: #e8e0d8; }
            .theme-classic .voucher-brand-pill, .theme-classic .voucher-batch-pill {  border-color: #1e3a5f;  color: #1e3a5f; }
            .theme-classic .voucher-type-pill { border-color: #1e3a5f; color: #1e3a5f; }
            .theme-classic .voucher-amount, .theme-classic .voucher-amount-symbol { color: #1a1a2e; }
            .theme-classic .voucher-code-box { border-color: #93c5fd; background: #eff6ff; color: #1e3a5f; }

            .theme-modern { background: linear-gradient(145deg, #eef2f7, #e2e8f0); border-color: #94a3b8; }
            .theme-modern .voucher-brand-pill, .theme-modern .voucher-batch-pill {  border-color: #1e40af; color: #1e40af; }
            .theme-modern .voucher-type-pill { border-color: #1e40af; color: #1e40af; }
            .theme-modern .voucher-amount, .theme-modern .voucher-amount-symbol { color: #0f172a; }
            .theme-modern .voucher-description { color: #475569; }
            .theme-modern .voucher-code-box { border-color: #60a5fa; background: #dbeafe; color: #1e3a8a; }
            .theme-modern .voucher-footer-row { border-color: #cbd5e1; color: #64748b; }

            .theme-elegant { background: linear-gradient(145deg, #1a1a2e, #16213e); border-color: #c9a227; }
            .theme-elegant .voucher-brand-pill, .theme-elegant .voucher-batch-pill { background: #c9a227; color: #1a1a2e; }
            .theme-elegant .voucher-type-pill { border-color: #c9a227; color: #c9a227; background: transparent; }
            .theme-elegant .voucher-amount, .theme-elegant .voucher-amount-symbol { color: #f5d76e; }
            .theme-elegant .voucher-description { color: #d4c5a0; }
            .theme-elegant .voucher-code-box { border-color: #c9a227; background: rgba(201,162,39,0.12); color: #f5d76e; }
            .theme-elegant .voucher-footer-row { border-color: #3d3a2e; color: #b8a88a; }

            .theme-holiday { background: linear-gradient(145deg, #fff5f5, #fef2f2); border-color: #c53030; }
            .theme-holiday .voucher-brand-pill, .theme-holiday .voucher-batch-pill { background: #c53030; color: #fff; }
            .theme-holiday .voucher-type-pill { border-color: #276749; color: #276749; }
            .theme-holiday .voucher-amount, .theme-holiday .voucher-amount-symbol { color: #9b2c2c; }
            .theme-holiday .voucher-description { color: #276749; }
            .theme-holiday .voucher-code-box { border-color: #fc8181; background: #fff5f5; color: #9b2c2c; }
            .theme-holiday .voucher-footer-row { border-color: #feb2b2; color: #9b2c2c; }

            .theme-minimal { background: #ffffff; border-color: #1a1a1a; border-width: 1.5px; }
            .theme-minimal .voucher-brand-pill, .theme-minimal .voucher-batch-pill { background: #111; color: #fff; }
            .theme-minimal .voucher-type-pill { border-color: #111; color: #111; background: transparent; }
            .theme-minimal .voucher-amount, .theme-minimal .voucher-amount-symbol { color: #111; }
            .theme-minimal .voucher-description { color: #555; }
            .theme-minimal .voucher-code-box { border-color: #999; background: #f5f5f5; color: #111; }
            .theme-minimal .voucher-footer-row { border-color: #e5e5e5; color: #666; }

            .print-footer {
              text-align: center;
              padding: 20px 0 10px;
              border-top: 2px solid #e8eaed;
              margin-top: 25px;
            }
            .print-footer p { color: #7f8c8d; font-size: 12px; margin: 4px 0; }
            .print-footer .footer-small { font-size: 10px; color: #95a5a6; }

            @media print {
              body { padding: 10px; background: white; }
              .voucher-card-inner { box-shadow: none; break-inside: avoid; page-break-inside: avoid; }
              .vouchers-grid:last-of-type { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>🎁 FURNITURE MECCA — GIFT VOUCHERS</h1>
            <p class="subtitle">Present this voucher to redeem your discount</p>
            <div class="batch-info">
              <span>📋 Batch: ${batchName}</span>
              <span>🆔 ${batchId}</span>
              <span>📅 ${created}</span>
              <span>🏷️ Discount: ${discount}$</span>
              <span>📊 ${this.printCardsData.length} vouchers</span>
              <span>📐 ${perPage} per page</span>
              <span>🎨 ${theme}</span>
            </div>
          </div>
      `;

      const totalCards = this.printCardsData.length;
      for (let i = 0; i < totalCards; i++) {
        if (i % perPage === 0) {
          if (i > 0) htmlContent += `</div>`;
          htmlContent += `<div class="vouchers-grid">`;
        }
        const card = this.printCardsData[i];
        const validityText = card.valid_until
          ? 'Valid until: ' + new Date(card.valid_until).toLocaleDateString()
          : 'No expiration';
        const assignedHtml = card.assigned_to_email
          ? `<span class="footer-item">✉ ${card.assigned_to_name || card.assigned_to_email}</span>`
          : '';

        const batchLabel = this.printBatchData ? (this.printBatchData.name || 'GIFT') : 'GIFT';
        htmlContent += `
          <div class="voucher-card-inner theme-${theme}">
            <div class="voucher-top-row">
              <span class="voucher-brand-pill">FURNITURE MECCA</span>
              <span class="voucher-type-pill">🎁 GIFT VOUCHER</span>
            </div>
            <div class="voucher-batch-pill">${batchLabel}</div>
            <div class="voucher-amount"><span class="voucher-amount-symbol">$</span>${this.roundAmount(card.amount)}</div>
            <div class="voucher-description">ON YOUR NEXT PURCHASE</div>
            <div class="voucher-code-box"># ${card.code}</div>
            <div class="voucher-footer-row">
              <span class="footer-item">📅 ${validityText}</span>
              <span class="footer-item">⚠ ONE TIME USE ONLY</span>
              ${assignedHtml}
            </div>
          </div>
        `;
      }

      htmlContent += `
          </div>
          <div class="print-footer">
            <p>❤️ Thank you for choosing Furniture Mecca!</p>
            <p class="footer-small">This is a system-generated document. Please verify all information.</p>
            <p class="footer-small">© ${new Date().getFullYear()} Furniture Mecca. All rights reserved.</p>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 400);
            };
          <\/script>
        </body>
        </html>
      `;

      doc.write(htmlContent);
      doc.close();
    },

    async sendEmails() {
      const cardsWithEmail = this.generatedCards.filter(c => c.assigned_to_email);
      if (cardsWithEmail.length === 0) {
        this.$bvToast.toast('No vouchers have email addresses assigned', {
          title: 'Info',
          variant: 'warning'
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Send ${cardsWithEmail.length} voucher(s) via email?`,
        { title: 'Send Vouchers', size: 'sm', okVariant: 'success', okTitle: 'SEND', cancelTitle: 'CANCEL' }
      );

      if (!confirmed) return;

      let sent = 0, failed = 0;
      for (const card of cardsWithEmail) {
        try {
          await Requestor.postAsyncP('/api/giftcard/send_email/', {
            gift_card: card,
            recipient_email: card.assigned_to_email,
            recipient_name: card.assigned_to_name || 'Valued Customer',
            message: this.form.design.message || '',
            store_name: 'Furniture Mecca',
            valid_until: card.valid_until
          }, this);
          sent++;
        } catch (error) {
          console.error('Failed to send email:', error);
          failed++;
        }
      }

      this.$bvToast.toast(
        `Sent ${sent} voucher${sent !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''}`,
        { title: 'Email Status', variant: failed > 0 ? 'warning' : 'success' }
      );
    },

    downloadCSV() {
      if (this.generatedCards.length === 0) return;

      const headers = ['Code', 'Discount $', 'Valid Until', 'Assigned To', 'Status'];
      const rows = this.generatedCards.map(card => [
        card.code, card.amount,
        card.valid_until || 'No expiration',
        card.assigned_to_email || 'Unassigned',
        card.status || 'Active'
      ]);

      let csv = headers.join(',') + '\n';
      rows.forEach(row => { csv += row.join(',') + '\n'; });

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vouchers_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },

    clearCards() { this.generatedCards = []; },

    getStatusVariant(status) {
      const variants = {
        'pending': 'secondary', 'generated': 'info', 'sent': 'primary',
        'redeemed': 'success', 'expired': 'danger', 'cancelled': 'danger'
      };
      return variants[status] || 'secondary';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('MM/DD/YYYY');
    },

    formatDateFull(date) {
      if (!date) return 'N/A';
      return moment(date).format('ddd, DD MMM YYYY HH:mm:ss [GMT]');
    }
  }
};
</script>

<style scoped>
/* ============================================
   GLASS CONTAINER
   ============================================ */
.glass-container {
  min-height: 100vh;
  padding: 20px;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.glass-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  z-index: 0;
}

.gift-card-generator {
  padding: 0;
  background: transparent;
  min-height: 100vh;
  z-index: 1;
  position: relative;
}

.main-container {
  padding: 24px;
  position: relative;
  z-index: 1;
}

/* ============================================
   MODERN CARD
   ============================================ */
.modern-card {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 25px;
  margin: 0 auto;
  margin-top: 10px;
  max-width: 1800px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-card {
  padding: 20px 28px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4a90e2, #667eea);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.page-subtitle {
  font-size: 14px;
  color: #6c7a8d;
  margin: 2px 0 0;
}

.header-stats {
  display: flex;
  gap: 12px;
}

.stat-chip {
  background: rgba(255, 255, 255, 0.8);
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.stat-icon { font-size: 14px; color: #4a6cf7; }
.chip-label { color: #6c7a8d; }
.chip-value { font-weight: 700; color: #1a1a2e; }

/* ============================================
   MAIN 2-COLUMN LAYOUT
   ============================================ */
.main-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.layout-col {
  min-width: 0;
}

.layout-col-form {
  flex: 0 0 41.666667%;
  max-width: 41.666667%;
}

.layout-col-preview {
  flex: 1 1 0;
  max-width: calc(58.333333% - 24px);
}

@media (max-width: 991px) {
  .layout-col-form,
  .layout-col-preview {
    flex: 1 1 100%;
    max-width: 100%;
  }
}

/* ============================================
   GLASS CARDS
   ============================================ */
.glass-card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  height: 100%;
}

.card-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(220, 226, 236, 0.3);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header-icon-wrapper {
  width: 32px;
  height: 32px;
  background: rgba(74, 108, 247, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6cf7;
}

.card-header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.step-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(74, 108, 247, 0.1);
  color: #4a6cf7;
}

.voucher-count-badge {
  font-size: 12px;
  padding: 6px 14px;
}

.card-body-custom { padding: 24px; }

/* ============================================
   GLASS INPUTS
   ============================================ */
.glass-input {
  background: rgba(255, 255, 255, 0.6);
  border-color: rgba(220, 226, 236, 0.4);
  transition: all 0.3s ease;
}

.glass-input:focus {
  background: rgba(255, 255, 255, 0.9);
  border-color: #4a6cf7;
  box-shadow: 0 0 0 0.2rem rgba(74, 108, 247, 0.15);
}

.glass-input-text {
  background: rgba(74, 108, 247, 0.06);
  border-color: rgba(220, 226, 236, 0.4);
  color: #4a6cf7;
}

.glass-checkbox {
  padding: 6px 12px;
  background: rgba(74, 108, 247, 0.04);
  border-radius: 6px;
  border: 1px solid rgba(74, 108, 247, 0.08);
}

/* ============================================
   FORM STYLES
   ============================================ */
.form-card .form-group {
  margin-bottom: 18px;
}

.form-card label {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 5px;
}

.counter-btn {
  background: rgba(255, 255, 255, 0.6);
  border-color: rgba(220, 226, 236, 0.4);
  color: #4a6cf7;
}

.counter-btn:hover {
  background: rgba(74, 108, 247, 0.1);
}

/* ============================================
   SINGLE LINE FORM
   ============================================ */
.single-line-form {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: nowrap;
  margin-bottom: 18px;
}

.single-field {
  margin-bottom: 0 !important;
}

.single-field >>> .form-group {
  margin-bottom: 0;
}

.single-field-batch {
  flex: 0 0 150px;
  max-width: 150px;
  min-width: 0;
}

.single-field-count {
  flex: 0 0 120px;
  min-width: 0;
}

.single-field-amount {
  flex: 0 0 100px;
  min-width: 0;
}

.single-field-validity {
  flex: 1 1 150px;
  min-width: 150px;
}

.validity-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  width: 100%;
}

.validity-inline .validity-checkbox {
  margin-bottom: 0;
  white-space: nowrap;
  flex: 0 0 auto;
}

.validity-inline .validity-date-group {
  flex: 1 1 140px;
  min-width: 140px;
  width: 140px;
}

.validity-inline .validity-date-group >>> .form-control {
  min-width: 140px;
}

@media (max-width: 1400px) {
  .single-field-batch { flex: 0 0 150px; }
  .single-field-count { flex: 0 0 110px; }
  .single-field-amount { flex: 0 0 90px; }
  .single-field-validity { flex: 1 1 150px; min-width: 150px; }
  .validity-inline .validity-date-group { min-width: 150px; }
}

@media (max-width: 1100px) {
  .single-line-form {
    flex-wrap: wrap;
  }
  .single-field-batch {
    flex: 1 1 100%;
    max-width: 100%;
  }
  .single-field-count,
  .single-field-amount {
    flex: 1 1 calc(50% - 5px);
  }
  .single-field-validity {
    flex: 1 1 100%;
    min-width: 0;
  }
  .validity-inline {
    width: 100%;
  }
  .validity-inline .validity-date-group {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
  }
}

@media (max-width: 576px) {
  .single-field-count,
  .single-field-amount {
    flex: 1 1 100%;
  }
  .validity-inline {
    flex-wrap: wrap;
  }
}

/* ============================================
   COLLAPSIBLE TEMPLATE SECTION (main form)
   ============================================ */
.template-section {
  margin-bottom: 18px;
}

.template-toggle-btn {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(74, 108, 247, 0.05);
  border: 1px solid rgba(74, 108, 247, 0.15);
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  transition: all 0.25s ease;
  text-align: left;
}

.template-toggle-btn:hover {
  background: rgba(74, 108, 247, 0.1);
  border-color: rgba(74, 108, 247, 0.3);
}

.template-toggle-btn:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(74, 108, 247, 0.15);
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.current-theme-tag {
  margin-left: 8px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  background: rgba(74, 108, 247, 0.12);
  color: #4a6cf7;
  letter-spacing: 0.3px;
}

.toggle-chevron {
  font-size: 14px;
  color: #4a6cf7;
  transition: transform 0.25s ease;
}

.template-section-body {
  margin-top: 10px;
  padding: 14px 14px 12px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(74, 108, 247, 0.1);
  border-radius: 10px;
  animation: templateFadeIn 0.25s ease;
}

@keyframes templateFadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================
   COLLAPSIBLE BATCH PRINT OPTIONS (modal)
   ============================================ */
.batch-print-options-collapsible {
  margin-bottom: 16px;
}

.batch-print-toggle-btn {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(74, 108, 247, 0.05);
  border: 1px solid rgba(74, 108, 247, 0.15);
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  transition: all 0.25s ease;
  text-align: left;
}

.batch-print-toggle-btn:hover {
  background: rgba(74, 108, 247, 0.1);
  border-color: rgba(74, 108, 247, 0.3);
}

.batch-print-toggle-btn:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(74, 108, 247, 0.15);
}

.batch-print-toggle-btn .toggle-label {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.batch-print-toggle-btn .current-theme-tag,
.batch-print-toggle-btn .current-size-tag {
  margin-left: 6px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  background: rgba(74, 108, 247, 0.12);
  color: #4a6cf7;
  letter-spacing: 0.3px;
}

.batch-print-toggle-btn .current-size-tag {
  background: rgba(40, 167, 69, 0.12);
  color: #28a745;
}

.batch-print-options-body {
  margin-top: 10px;
  padding: 14px 16px;
  background: rgba(74, 108, 247, 0.04);
  border: 1px solid rgba(74, 108, 247, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: templateFadeIn 0.25s ease;
}

/* ============================================
   ASSIGN CUSTOMER ROW
   ============================================ */
.assign-customer-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.assign-customer-row > .input-group {
  flex: 1 1 calc(50% - 6px);
  min-width: 0;
}

@media (max-width: 576px) {
  .assign-customer-row > .input-group {
    flex: 1 1 100%;
  }
}

.send-email-checkbox {
  padding: 6px 12px;
  background: rgba(40, 167, 69, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(40, 167, 69, 0.1);
}

/* ============================================
   QUICK STATS
   ============================================ */
.quick-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  background: rgba(74, 108, 247, 0.04);
  border-radius: 10px;
  border: 1px solid rgba(74, 108, 247, 0.08);
}

.quick-stats .stat-item {
  text-align: center;
}

.quick-stats .stat-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  color: #8a9aa8;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.quick-stats .stat-number {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  margin-top: 2px;
}

/* ============================================
   GENERATE BUTTON
   ============================================ */
.generate-btn {
  margin-top: 8px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #28a745, #20c997);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(40, 167, 69, 0.25);
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(40, 167, 69, 0.35);
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ============================================
   PRINT SIZE SELECTOR
   ============================================ */
.print-size-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: rgba(74, 108, 247, 0.04);
  border-radius: 8px;
  border: 1px solid rgba(74, 108, 247, 0.08);
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.selector-label {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  display: flex;
  align-items: center;
}

.size-btn {
  min-width: 36px;
  font-weight: 600;
}

/* ============================================
   VOUCHERS GRID
   ============================================ */
.vouchers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  max-height: 520px;
  overflow-y: auto;
  padding: 4px 4px 4px 0;
}

.vouchers-grid::-webkit-scrollbar {
  width: 5px;
}

.vouchers-grid::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
}

.vouchers-grid::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4a90e2, #667eea);
  border-radius: 10px;
}

.voucher-card {
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-slide-up {
  opacity: 0;
  animation: slideUp 0.6s ease forwards;
}

.voucher-card-inner {
  background: #faf7f2;
  border-radius: 14px;
  padding: 18px 20px 14px;
  text-align: center;
  border: 1px solid #e8e0d8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.voucher-card-inner:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 28px rgba(74, 108, 247, 0.12);
  border-color: #4a90e2;
}

.voucher-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
}

.voucher-brand-pill {
  background: #fff;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 4px 12px;
  border-radius: 4px;
  text-transform: uppercase;
}

.voucher-type-pill {
  border: 1.5px solid #1e3a5f;
  color: #1e3a5f;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  border-radius: 4px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.voucher-batch-pill {
  background: #fff;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 3px 14px;
  border-radius: 4px;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.voucher-amount {
  font-size: 42px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.1;
  margin: 4px 0;
}

.voucher-amount-symbol {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  margin-right: 1px;
}

.voucher-description {
  font-size: 12px;
  font-weight: 600;
  color: #6c7a8d;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin: 2px 0 10px;
}

.voucher-code-box {
  font-size: 12px;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #1e3a5f;
  border: 1.5px dashed #93c5fd;
  background: #eff6ff;
  padding: 5px 16px;
  border-radius: 20px;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.voucher-footer-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px 18px;
  width: 100%;
  padding-top: 10px;
  border-top: 1px solid #ebe4dc;
  font-size: 11px;
  color: #6c7a8d;
}

.footer-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

/* ========== THEME VARIANTS ========== */
.theme-classic {
  background: #faf7f2;
  border-color: #e8e0d8;
}
.theme-classic .voucher-brand-pill,
.theme-classic .voucher-batch-pill { border-color: #1e3a5f; color: #1e3a5f;  }
.theme-classic .voucher-type-pill { border-color: #1e3a5f; color: #1e3a5f; }
.theme-classic .voucher-amount,
.theme-classic .voucher-amount-symbol { color: #1a1a2e; }
.theme-classic .voucher-code-box { border-color: #93c5fd; background: #eff6ff; color: #1e3a5f; }

.theme-modern {
  background: linear-gradient(145deg, #eef2f7, #e2e8f0);
  border-color: #94a3b8;
}
.theme-modern .voucher-brand-pill,
.theme-modern .voucher-batch-pill { background: #1e40af; color: #fff; }
.theme-modern .voucher-type-pill { border-color: #1e40af; color: #1e40af; }
.theme-modern .voucher-amount,
.theme-modern .voucher-amount-symbol { color: #0f172a; }
.theme-modern .voucher-description { color: #475569; }
.theme-modern .voucher-code-box { border-color: #60a5fa; background: #dbeafe; color: #1e3a8a; }
.theme-modern .voucher-footer-row { border-color: #cbd5e1; color: #64748b; }

.theme-elegant {
  background: linear-gradient(145deg, #1a1a2e, #16213e);
  border-color: #c9a227;
  color: #f5f0e6;
}
.theme-elegant .voucher-brand-pill,
.theme-elegant .voucher-batch-pill { background: #c9a227; color: #1a1a2e; }
.theme-elegant .voucher-type-pill { border-color: #c9a227; color: #c9a227; background: transparent; }
.theme-elegant .voucher-amount,
.theme-elegant .voucher-amount-symbol { color: #f5d76e; }
.theme-elegant .voucher-description { color: #d4c5a0; }
.theme-elegant .voucher-code-box { border-color: #c9a227; background: rgba(201,162,39,0.12); color: #f5d76e; }
.theme-elegant .voucher-footer-row { border-color: #3d3a2e; color: #b8a88a; }

.theme-holiday {
  background: linear-gradient(145deg, #fff5f5, #fef2f2);
  border-color: #c53030;
}
.theme-holiday .voucher-brand-pill,
.theme-holiday .voucher-batch-pill { background: #c53030; color: #fff; }
.theme-holiday .voucher-type-pill { border-color: #276749; color: #276749; }
.theme-holiday .voucher-amount,
.theme-holiday .voucher-amount-symbol { color: #9b2c2c; }
.theme-holiday .voucher-description { color: #276749; }
.theme-holiday .voucher-code-box { border-color: #fc8181; background: #fff5f5; color: #9b2c2c; }
.theme-holiday .voucher-footer-row { border-color: #feb2b2; color: #9b2c2c; }

.theme-minimal {
  background: #ffffff;
  border-color: #1a1a1a;
  border-width: 1.5px;
}
.theme-minimal .voucher-brand-pill,
.theme-minimal .voucher-batch-pill { background: #111111; color: #fff; }
.theme-minimal .voucher-type-pill { border-color: #111111; color: #111111; background: transparent; }
.theme-minimal .voucher-amount,
.theme-minimal .voucher-amount-symbol { color: #111111; }
.theme-minimal .voucher-description { color: #555555; }
.theme-minimal .voucher-code-box { border-color: #999; background: #f5f5f5; color: #111; }
.theme-minimal .voucher-footer-row { border-color: #e5e5e5; color: #666; }

/* ========== TEMPLATE PICKER ========== */
.template-picker {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 4px;
}

.template-option {
  cursor: pointer;
  text-align: center;
  border-radius: 10px;
  padding: 6px 4px 8px;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.5);
  transition: all 0.2s ease;
}

.template-option:hover {
  background: rgba(255, 255, 255, 0.85);
  transform: translateY(-2px);
}

.template-option.active {
  border-color: #4a6cf7;
  background: rgba(74, 108, 247, 0.08);
  box-shadow: 0 2px 10px rgba(74, 108, 247, 0.15);
}

.template-swatch {
  height: 52px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 800;
  font-size: 14px;
}

.swatch-classic {
  background: linear-gradient(145deg, #fdf8f3, #f5ede4);
  color: #1a1a2e;
  border-color: #e8ddd0;
}
.swatch-modern {
  background: linear-gradient(145deg, #eef2f7, #e2e8f0);
  color: #0f172a;
  border-color: #94a3b8;
}
.swatch-elegant {
  background: linear-gradient(145deg, #1a1a2e, #16213e);
  color: #f5d76e;
  border-color: #c9a227;
}
.swatch-holiday {
  background: linear-gradient(145deg, #fff5f5, #fef2f2);
  color: #9b2c2c;
  border-color: #c53030;
}
.swatch-minimal {
  background: #ffffff;
  color: #111111;
  border-color: #1a1a1a;
}

.template-name {
  font-size: 11px;
  font-weight: 600;
  color: #1a1a2e;
  display: block;
}

.swatch-amount {
  letter-spacing: -0.5px;
}

/* ============================================
   ACTION BAR
   ============================================ */
.action-bar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.action-bar .btn {
  font-size: 13px;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state {
  text-align: center;
  padding: 50px 20px;
}

.empty-icon {
  color: rgba(74, 108, 247, 0.15);
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 16px 0 8px;
}

.empty-text {
  color: #8a9aa8;
  margin-bottom: 0;
}

/* ============================================
   TABLE
   ============================================ */
.table-card {
  height: 100%;
}

.glass-table >>> thead th {
  background: rgba(244, 247, 252, 0.6) !important;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  padding: 12px 16px;
}

.glass-table >>> tbody td {
  padding: 12px 16px;
  vertical-align: middle;
  border-color: rgba(226, 232, 240, 0.3);
}

.glass-table >>> tbody tr {
  background: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.glass-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.6) !important;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-dot {
  font-size: 8px;
}

.batch-select-checkbox {
  margin: 0;
  cursor: pointer;
}

.action-icon-btn {
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  margin: 0 2px;
}

.action-icon-btn:hover {
  transform: scale(1.1);
}

/* ============================================
   BATCH PRINT MODAL
   ============================================ */
.batch-print-container {
  padding: 20px;
  background: white;
}

.batch-option-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.template-picker-sm .template-swatch {
  height: 40px;
  font-size: 12px;
}

.template-picker-sm .template-name {
  font-size: 10px;
}

.no-print { /* screen only */ }

.batch-print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 20px;
  border-bottom: 3px solid #2c3e50;
  margin-bottom: 20px;
}

.print-store-name {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.print-store-address {
  color: #7f8c8d;
  margin: 4px 0;
}

.print-store-phone {
  color: #7f8c8d;
  margin: 4px 0;
}

.print-header-right {
  text-align: right;
}

.print-batch-badge {
  background: #2c3e50;
  color: white;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
}

.print-batch-id {
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin-top: 8px;
}

.print-batch-date {
  color: #7f8c8d;
  font-size: 13px;
  margin-top: 4px;
}

.print-divider {
  border: none;
  border-top: 2px dashed #e0e0e0;
  margin: 20px 0;
}

.batch-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
}

.info-item {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #7f8c8d;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
}

.status-badge-print {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  background: #d4edda;
  color: #155724;
}

.print-vouchers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 20px 0;
}

.print-vouchers-grid .voucher-card-inner {
  min-height: 260px;
  padding: 24px 16px 16px;
}

.print-voucher-item {
  break-inside: avoid;
}

.print-footer {
  text-align: center;
  padding: 20px 0 10px;
  border-top: 2px solid #e8eaed;
  margin-top: 20px;
}

.print-footer p {
  color: #7f8c8d;
  font-size: 13px;
  margin: 4px 0;
}

.print-footer-small {
  font-size: 11px;
  color: #95a5a6;
}

.print-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 768px) {
  .main-container { padding: 16px; }
  .page-title { font-size: 20px; }
  .vouchers-grid { grid-template-columns: 1fr; max-height: 400px; gap: 12px; }
  .voucher-card-inner { padding: 14px 14px 10px; min-height: auto; }
  .voucher-amount { font-size: 36px; }
  .voucher-amount-symbol { font-size: 18px; }
  .action-bar { flex-direction: row; flex-wrap: wrap; }
  .action-bar .btn { flex: 1; min-width: 80px; font-size: 12px; }
  .batch-print-header { flex-direction: column; }
  .print-header-right { text-align: left; margin-top: 10px; }
  .batch-info-grid { grid-template-columns: 1fr 1fr; }
  .print-vouchers-grid { grid-template-columns: 1fr; }
  .header-stats { width: 100%; justify-content: flex-start; }
  .header-actions { flex-wrap: wrap; gap: 8px; }
  .print-preview-btn { font-size: 11px; padding: 4px 10px; }
  .quick-stats { grid-template-columns: 1fr 1fr; }
  .print-size-selector { flex-direction: column; align-items: stretch; }
  .print-size-selector .b-button-group { justify-content: center; }
  .template-picker { grid-template-columns: repeat(3, 1fr); gap: 6px; }
  .template-swatch { height: 44px; font-size: 12px; }
  .template-name { font-size: 10px; }
}

@media (max-width: 480px) {
  .vouchers-grid { grid-template-columns: 1fr; }
  .batch-info-grid { grid-template-columns: 1fr; }
  .action-bar .btn { min-width: 100%; }
  .quick-stats { grid-template-columns: 1fr; gap: 6px; }
  .quick-stats .stat-number { font-size: 16px; }
  .header-content { flex-direction: column; align-items: flex-start; }
  .header-stats { flex-direction: column; width: 100%; }
  .stat-chip { width: 100%; justify-content: center; }
  .template-picker { grid-template-columns: repeat(2, 1fr); }
}
</style>


// WEBPACK FOOTER //
// src/components/GiftCard/GenerateGiftCards.vue